import { useQuery } from '@tanstack/react-query';
import { searchApi, SearchTasksParams } from '@/lib/api';
import { queryKeys } from '@/lib/queryClient';
import type { TaskSearchResponse } from '@/types';

export interface SearchFilters {
  q?: string;
  projectId?: string;
  listId?: string;
  statusId?: string;
  assigneeId?: string;
  dueBefore?: string;
  dueAfter?: string;
  closedBefore?: string;
  closedAfter?: string;
  tagIds?: string[];
  useDateClosed?: boolean;
  /** When true, show only tasks I follow (uses /tasks/following endpoint instead of search). */
  followingOnly?: boolean;
}

function hasActiveFilters(filters: SearchFilters): boolean {
  return !!(
    filters.q?.trim() ||
    filters.projectId ||
    filters.listId ||
    filters.statusId ||
    filters.assigneeId ||
    filters.dueBefore ||
    filters.dueAfter ||
    (filters.tagIds && filters.tagIds.length > 0)
  );
}

export function useSearchTasks(filters: SearchFilters) {
  const params: SearchTasksParams = {
    q: filters.q?.trim() || undefined,
    projectId: filters.projectId || undefined,
    listId: filters.listId || undefined,
    statusId: filters.statusId || undefined,
    assigneeId: filters.assigneeId || undefined,
    dueBefore: filters.useDateClosed ? undefined : filters.dueBefore || undefined,
    dueAfter: filters.useDateClosed ? undefined : filters.dueAfter || undefined,
    closedBefore: filters.useDateClosed ? filters.dueBefore || undefined : undefined,
    closedAfter: filters.useDateClosed ? filters.dueAfter || undefined : undefined,
    tagIds: filters.tagIds?.length ? filters.tagIds : undefined,
  };

  return useQuery({
    queryKey: queryKeys.search.tasks(filters.q || '', params as Record<string, unknown>),
    queryFn: async () => {
      const response = await searchApi.searchTasks(params);
      return response.data as TaskSearchResponse;
    },
    enabled: hasActiveFilters(filters),
  });
}
